import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { Comment } from './comment';
import { getMentionedUsers, MENTION_PATTERN } from '@frontend-site/mention/utils';
import { SessionRepository, UserGroupStore, UsersStore, ProjectStore } from '@frontend-site/shared/core';
import { User } from '@frontend-site/shared/data';
import { Group } from '@frontend-site/shared/data';
import { WatcherData } from './watcherData';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class CommentsService {
    private usersSubject = new BehaviorSubject<(User | Group)[]>([]);
    public users$ = this.usersSubject.asObservable();

    constructor(
        private readonly sessionStore: SessionRepository,
        private readonly usersStore: UsersStore,
        private readonly userGroupStore: UserGroupStore,
        private projec: ProjectStore,
        private http: HttpClient
    ) {
        combineLatest([this.usersStore.activeUsers$, this.userGroupStore.groupsUsers$])
            .pipe(
                map(([users, groups]) => {
                    return [...users, ...groups];
                })
            )
            .subscribe((combinedUsers) => {
                this.usersSubject.next(combinedUsers);
            });
    }

    public prepareSave(comments: Comment[], watcherData: WatcherData): { comments: Comment[]; watcherData: WatcherData } {
        comments
            .filter((c) => c.state === 'NEW' || c.state === 'EDITED')
            .forEach((comment) => {
                this.subscribeUser(this.sessionStore.applicationConfig?.user, watcherData);
                const mentioned = getMentionedUsers(comment.text, this.usersSubject.value);
                this.subscribeUsers(mentioned.users, watcherData);
                this.subscribeGroups(mentioned.groups, watcherData);
            });

        return { comments: comments.filter((c) => _.includes(['NEW', 'EDITED', 'DELETED'], c.state)), watcherData };
    }

    createComment(
        text: string = '',
        mentionedUsers: Map<string, User> = new Map<string, User>(),
        mentionedGroups: Map<number, Group> = new Map<number, Group>(),
        state: string = 'NEW'
    ) {
        const comment = new Comment();
        comment.state = state;
        comment.text = text;
        comment._mentionedUsers = mentionedUsers;
        comment._mentionedGroups = mentionedGroups;
        return comment;
    }

    public subscribeUser(user: User | undefined, watcherData: WatcherData): WatcherData {
        if (user && !this.isExtraWatcher(user, watcherData) && !this.isBaseWatcher(user, watcherData)) {
            watcherData.extraWatchers?.push(user);
            watcherData.watchersChanged = true;
        }

        return watcherData;
    }

    private subscribeUsers(mentioned: User[], watcherData: WatcherData): void {
        if (mentioned) {
            mentioned.forEach((user) => this.subscribeUser(user, watcherData));
        }
    }

    private subscribeGroups(mentionedGroups: Group[], watcherData: WatcherData): void {
        if (mentionedGroups) {
            mentionedGroups.forEach((userGroup) => this.subscribeGroup(userGroup, watcherData));
        }
    }

    private subscribeGroup(group: Group, watcherData: WatcherData): WatcherData {
        if (!this.isExtraGroupsWatcher(group, watcherData)) {
            watcherData.groupsWatchers?.push(group);
            watcherData.watchersChanged = true;
        }

        return watcherData;
    }

    unsubscribeUser(user: User, watcherData: WatcherData) {
        if (this.isExtraWatcher(user, watcherData)) {
            watcherData.extraWatchers = _.reject(watcherData.extraWatchers, { id: user.id });
            watcherData.watchersChanged = true;
        }

        return watcherData;
    }

    isBaseWatcher(user: User, watcherData: WatcherData) {
        const { id } = user;
        return !!_.find(watcherData?.baseWatchers, { id });
    }

    isExtraWatcher(user: User, watcherData: WatcherData) {
        const { id } = user;
        return !!_.find(watcherData?.extraWatchers, { id });
    }

    isExtraGroupsWatcher(group: Group, watcherData: WatcherData) {
        const { id } = group;
        return !!_.find(watcherData?.groupsWatchers, { id });
    }

    processComment(text: string): Array<{ type: string; value: string }> {
        const mentions = text.match(MENTION_PATTERN);
        return this.split(text, mentions);
    }

    private split(text: string, mentions: RegExpMatchArray | null): Array<{ type: string; value: string }> {
        const arr: Array<{ type: string; value: string }> = [];
        let currTextPosition = 0;

        while (currTextPosition < text.length) {
            let mention = mentions?.shift();
            currTextPosition += this.addArray(arr, text.substr(currTextPosition), mention);
        }
        return arr;
    }

    private addArray(array: Array<{ type: string; value: string }>, text: string, mention: string | undefined): number {
        let newTextPosition;
        if (mention) {
            const indexOf = text.indexOf(mention);
            if (indexOf > 0) {
                array.push({ type: 'regular', value: text.substr(0, indexOf) });
            }
            array.push({ type: 'chip', value: mention.substr(2, mention.length - 3) }); //remove "[~" and "]"
            newTextPosition = indexOf + mention.length;
        } else {
            array.push({ type: 'regular', value: text });
            newTextPosition = text.length;
        }
        return newTextPosition;
    }

    public changeSortDirection(ascending: boolean): Observable<boolean> {
        const projectId = this.projec.projectId || 0;
        const params = new HttpParams().set('ascending', ascending).set('projectId', projectId.toString());
        return this.http.post<boolean>('/rest/comment/sortDirection', null, { params });
    }
}

export enum CommentsSortType {
    NEWEST_FIRST,
    OLDEST_FIRST
}
